import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const API = axios.create({ baseURL: '/api', withCredentials: true });

const WEIGHT_BREAKS = [0, 100, 1000, 2000, 5000];

const RATE_FIELDS = [
  'minimum_charge', 'rate_per_cwt', 'liftgate_fee', 'residential_min',
  'residential_cwt', 'two_man_flat', 'two_man_per_mile',
  'special_multiplier', 'special_normal_flat', 'special_after_hours_flat', 'special_weekend_flat'
];

const emptyRate = {
  zone: '', weight_break: 0, minimum_charge: 0, rate_per_cwt: 0,
  liftgate_fee: 0, residential_min: 0, residential_cwt: 0,
  two_man_flat: 0, two_man_per_mile: 0,
  special_multiplier: 2, special_normal_flat: 0, special_after_hours_flat: 0, special_weekend_flat: 0
};

const fmtRate = (field, v) => {
  const num = parseFloat(v) || 0;
  if (field === 'special_multiplier') return `${num}×`;
  const dp = ['rate_per_cwt', 'residential_cwt', 'two_man_per_mile'].includes(field) ? 4 : 2;
  return `$${num.toFixed(dp)}`;
};

const ZoneRates = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [rates, setRates] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editRow, setEditRow] = useState({});
  const [customerName, setCustomerName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filterZone, setFilterZone] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newRate, setNewRate] = useState({ ...emptyRate });

  // eslint-disable-next-line
  useEffect(() => { fetchData(); }, [customerId]);

  const fetchData = async () => {
    try {
      const [ratesRes, custRes] = await Promise.all([
        API.get(`/customers/${customerId}/zone-rates`),
        API.get('/customers')
      ]);
      setRates(ratesRes.data);
      const cust = custRes.data.find(c => c.customer_id === parseInt(customerId));
      if (cust) setCustomerName(cust.name);
    } catch {
      toast.error('Failed to load zone rates');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (rate) => { setEditingId(rate.id); setEditRow({ ...rate }); };
  const cancelEdit = () => { setEditingId(null); setEditRow({}); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const res = await API.put(`/customers/${customerId}/zone-rates/${editingId}`, editRow);
      setRates(prev => prev.map(r => r.id === editingId ? res.data : r));
      setEditingId(null);
      toast.success('Rate updated');
    } catch {
      toast.error('Failed to update rate');
    } finally {
      setSaving(false);
    }
  };

  const deleteRate = async (rateId) => {
    if (!window.confirm('Delete this rate row?')) return;
    try {
      await API.delete(`/customers/${customerId}/zone-rates/${rateId}`);
      setRates(prev => prev.filter(r => r.id !== rateId));
      toast.success('Rate deleted');
    } catch {
      toast.error('Failed to delete rate');
    }
  };

  const addRate = async () => {
    if (!newRate.zone) return toast.error('Zone is required');
    setSaving(true);
    try {
      const res = await API.post(`/customers/${customerId}/zone-rates`, newRate);
      setRates(prev => [...prev, res.data].sort((a, b) =>
        a.zone.localeCompare(b.zone) || a.weight_break - b.weight_break
      ));
      setShowAddModal(false);
      setNewRate({ ...emptyRate });
      toast.success('Rate added');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add rate');
    } finally {
      setSaving(false);
    }
  };

  const zones = [...new Set(rates.map(r => r.zone))].sort();
  const filtered = filterZone ? rates.filter(r => r.zone === filterZone) : rates;

  const thStyle = {
    padding: '10px 8px', background: '#1A2A3A', color: 'white',
    fontSize: '12px', fontWeight: '500', textAlign: 'left',
    borderRight: '1px solid #F5A800', whiteSpace: 'nowrap'
  };

  const tdStyle = {
    padding: '7px 8px', borderBottom: '1px solid #E2E8F0',
    borderRight: '1px solid #f0f0f0', fontSize: '13px'
  };

  const inputStyle = {
    width: '80px', padding: '4px 6px', border: '1px solid #E8B84B',
    borderRadius: '4px', fontSize: '12px', outline: 'none', background: '#FDF4DD'
  };

  const modalInputStyle = {
    width: '100%', padding: '8px 10px', border: '1px solid #E2E8F0',
    borderRadius: '6px', fontSize: '14px', outline: 'none'
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;

  return (
    <div className="fcc-fade-in" style={{ padding: '24px', maxWidth: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '600' }}>Zone Rates</h1>
          <p style={{ color: '#5B6B7F', fontSize: '14px' }}>Customer: <strong>{customerName}</strong></p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => navigate('/dashboard')} className="fcc-press" style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px', color: '#0F1B2D' }}>
            ← Dashboard
          </button>
          <button onClick={() => setShowAddModal(true)} className="btn btn-primary fcc-press" style={{ width: 'auto', padding: '8px 16px' }}>
            + Add Rate
          </button>
        </div>
      </div>

      <div style={{ background: '#FDF4DD', border: '1px solid #F2D89A', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: '#7A5C00' }}>
        <strong>Special charges:</strong> for Zones A–D the three flat rates are used (Normal / After Hours / Weekend-Holiday). For Zones E–Z the charge is <em>approx miles × Spc ×</em> (the multiplier). These values are flat per zone, so set the same numbers on each weight-break row of a given zone.
      </div>

      {/* Zone filter */}
      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: '13px', fontWeight: '500', color: '#5B6B7F' }}>Filter:</span>
        <button onClick={() => setFilterZone('')} className="fcc-press" style={{ padding: '4px 12px', borderRadius: '4px', border: '1px solid #E2E8F0', background: !filterZone ? '#1A2A3A' : 'white', color: !filterZone ? 'white' : '#0F1B2D', cursor: 'pointer', fontSize: '13px' }}>All</button>
        {zones.map(z => (
          <button key={z} onClick={() => setFilterZone(z)} className="fcc-press" style={{ padding: '4px 12px', borderRadius: '4px', border: '1px solid #E2E8F0', background: filterZone === z ? '#1A2A3A' : 'white', color: filterZone === z ? 'white' : '#0F1B2D', cursor: 'pointer', fontSize: '13px' }}>{z}</button>
        ))}
      </div>

      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Zone</th>
                <th style={thStyle}>Wt Break</th>
                <th style={thStyle}>Min Charge</th>
                <th style={thStyle}>Rate/CWT</th>
                <th style={thStyle}>Liftgate</th>
                <th style={thStyle}>Res Min</th>
                <th style={thStyle}>Res CWT</th>
                <th style={thStyle}>2-Man Flat</th>
                <th style={thStyle}>2-Man/Mile</th>
                <th style={thStyle}>Spc ×</th>
                <th style={thStyle}>Spc Normal</th>
                <th style={thStyle}>Spc After</th>
                <th style={thStyle}>Spc Wknd</th>
                <th style={{ ...thStyle, borderRight: 'none' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(rate => {
                const isEditing = editingId === rate.id;
                const row = isEditing ? editRow : rate;
                return (
                  <tr key={rate.id} style={{ background: isEditing ? '#FDF4DD' : 'white' }}>
                    <td style={{ ...tdStyle, fontWeight: '600' }}>{row.zone}</td>
                    <td style={tdStyle}>{row.weight_break} lbs</td>
                    {RATE_FIELDS.map(field => (
                      <td key={field} style={tdStyle}>
                        {isEditing
                          ? <input style={inputStyle} type="number" step="0.0001" value={row[field] ?? 0} onChange={e => setEditRow(prev => ({ ...prev, [field]: e.target.value }))} />
                          : fmtRate(field, row[field])
                        }
                      </td>
                    ))}
                    <td style={{ ...tdStyle, borderRight: 'none', whiteSpace: 'nowrap' }}>
                      {isEditing ? (
                        <>
                          <button onClick={saveEdit} disabled={saving} className="fcc-press" style={{ background: '#15803D', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', marginRight: '4px' }}>Save</button>
                          <button onClick={cancelEdit} className="fcc-press" style={{ background: '#5B6B7F', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(rate)} className="fcc-press" style={{ background: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', marginRight: '4px' }}>Edit</button>
                          <button onClick={() => deleteRate(rate.id)} className="fcc-press" style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}>Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Rate Modal */}
      {showAddModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="fcc-modal-pop" style={{ background: 'white', borderRadius: '12px', padding: '28px', width: '560px', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>Add Zone Rate</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Zone *</label>
                <input style={modalInputStyle} placeholder="e.g. A" maxLength={1} value={newRate.zone} onChange={e => setNewRate(prev => ({ ...prev, zone: e.target.value.toUpperCase() }))} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>Weight Break *</label>
                <select style={modalInputStyle} value={newRate.weight_break} onChange={e => setNewRate(prev => ({ ...prev, weight_break: parseInt(e.target.value) }))}>
                  {WEIGHT_BREAKS.map(w => <option key={w} value={w}>{w} lbs</option>)}
                </select>
              </div>
              {[
                { key: 'minimum_charge', label: 'Minimum Charge ($)' },
                { key: 'rate_per_cwt', label: 'Rate per CWT ($)' },
                { key: 'liftgate_fee', label: 'Liftgate Fee ($)' },
                { key: 'residential_min', label: 'Residential Min ($)' },
                { key: 'residential_cwt', label: 'Residential CWT ($)' },
                { key: 'two_man_flat', label: 'Two-Man Flat ($)' },
                { key: 'two_man_per_mile', label: 'Two-Man Per Mile ($)' },
                { key: 'special_multiplier', label: 'Special Multiplier (× miles, E–Z)' },
                { key: 'special_normal_flat', label: 'Special — Normal Hours ($, A–D)' },
                { key: 'special_after_hours_flat', label: 'Special — After Hours ($, A–D)' },
                { key: 'special_weekend_flat', label: 'Special — Weekend/Holiday ($, A–D)' },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>{f.label}</label>
                  <input style={modalInputStyle} type="number" step="0.0001" value={newRate[f.key]} onChange={e => setNewRate(prev => ({ ...prev, [f.key]: e.target.value }))} placeholder="0.00" />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowAddModal(false); setNewRate({ ...emptyRate }); }} className="fcc-press" style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '9px 18px', cursor: 'pointer', fontSize: '14px', color: '#0F1B2D' }}>Cancel</button>
              <button onClick={addRate} disabled={saving} className="btn btn-primary fcc-press" style={{ width: 'auto', padding: '9px 20px' }}>
                {saving ? 'Adding...' : 'Add Rate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZoneRates;
